import { DiscordSDK, Events } from "@discord/embedded-app-sdk";
import { IN_DISCORD, getJson, postJson } from "./api";
import { initLocale } from "./i18n";
import { setReportContext } from "./report";

export interface AppUser {
  id: string;
  name: string;
  avatarUrl: string | null;
}

export interface AppSession {
  /** Opaque credential for the websocket. */
  session: string;
  user: AppUser;
  /**
   * The room key. Inside Discord this is the voice-call activity instance, so
   * everyone in the same call lands in the same room automatically.
   */
  instanceId: string;
  inDiscord: boolean;
  /** Whether the server has somewhere to forward feedback to. */
  feedbackEnabled: boolean;
  /** Our own origin, for links that must open outside the Discord iframe. */
  publicUrl: string;
  /** Whether Discord accepted interactive picture-in-picture. */
  pip?: PipState;
  /**
   * The link that brings someone else to this room, for a map started in a
   * browser. null inside Discord, where the room is the voice call and the
   * way in is the call itself.
   */
  roomUrl?: string | null;
  /** Short code that ties this session to its lines in the server log. */
  trace: string | null;
}

export interface PipState {
  interactive: boolean;
  /** Why Discord refused, when it did. */
  error: string | null;
  /** Which client asked. Interactive PiP is not offered on every platform. */
  platform: string;
}

interface ServerConfig {
  clientId: string;
  allowAnonymous: boolean;
  feedback?: boolean;
  publicUrl?: string;
  installUrl?: string;
}

/** Thrown when the app is opened outside Discord with no invite or handoff. */
export class NotInDiscord extends Error {
  constructor(readonly installUrl: string) {
    super("Opened outside Discord");
    this.name = "NotInDiscord";
  }
}

interface SessionResponse {
  session: string;
  user: AppUser;
  access_token?: string;
  /** Short code a person can quote in a bug report. */
  trace?: string;
}

/**
 * Kept so the rest of the app can ask Discord to open a link. `window.open` is
 * blocked inside the Activity iframe; this command is the sanctioned route.
 */
let sdkRef: DiscordSDK | null = null;

export async function openExternal(url: string): Promise<void> {
  if (sdkRef) {
    await sdkRef.commands.openExternalLink({ url });
    return;
  }
  window.open(url, "_blank", "noopener");
}

/**
 * Asks Discord to keep the Activity usable once a screen share takes the stage.
 *
 * Without this the Activity is demoted to a picture-in-picture thumbnail you
 * can drag around but not click. It is deliberately called *after*
 * `authenticate`: several commands are refused before the handshake completes,
 * and calling it too early is silently useless.
 *
 * The outcome is returned rather than swallowed. When it fails there is no way
 * to tell from the outside why the window is dead, and guessing is expensive.
 */
async function enableInteractivePip(sdk: DiscordSDK): Promise<PipState> {
  const apply = () => sdk.commands.setConfig({ use_interactive_pip: true });

  const platform = String(sdk.platform ?? "unknown");

  let state: PipState;
  try {
    const result = await apply();
    state = { interactive: result.use_interactive_pip === true, error: null, platform };
  } catch (cause) {
    state = {
      interactive: false,
      error: cause instanceof Error ? cause.message : String(cause),
      platform,
    };
  }

  // Some clients only honour the setting once they are actually in that layout,
  // so ask again on the way in. Failures here are not worth reporting twice.
  void sdk
    .subscribe(Events.ACTIVITY_LAYOUT_MODE_UPDATE, () => {
      void apply().catch(() => null);
    })
    .catch(() => null);

  return state;
}

/**
 * Remembers the credential for this tab.
 *
 * Every load used to trade a fresh authorization code for a token, and Discord
 * rate limits that per application: with a few hundred people open at once, a
 * wave of reloads is enough to earn "You are being rate limited for requesting
 * too many tokens", which reaches people as a 502 and a map that will not
 * start. Nothing about the credential needs to be new on a reload -- it is
 * valid for twelve hours -- so a tab exchanges once and reuses it.
 *
 * sessionStorage, not localStorage: this dies with the tab, which is the right
 * lifetime for something that stands in for a login. It holds no more than the
 * page already held a second ago.
 */
const CREDENTIAL_KEY = "callout:credential";

interface CachedCredential {
  session: string;
  trace: string | null;
  user: AppUser;
  accessToken: string | null;
  /** Discarded well before the server would reject it. */
  expiresAt: number;
}

function readCredential(clientId: string): CachedCredential | null {
  try {
    const raw = window.sessionStorage.getItem(`${CREDENTIAL_KEY}:${clientId}`);
    if (!raw) return null;
    const cached = JSON.parse(raw) as CachedCredential;
    if (typeof cached?.session !== "string" || typeof cached.expiresAt !== "number") return null;
    if (cached.expiresAt < Date.now()) return null;
    return cached;
  } catch {
    return null;
  }
}

function writeCredential(clientId: string, value: CachedCredential): void {
  try {
    window.sessionStorage.setItem(`${CREDENTIAL_KEY}:${clientId}`, JSON.stringify(value));
  } catch {
    // A private window refusing storage costs one extra exchange, not a failure.
  }
}

/**
 * What the browser window got when it traded the code in.
 *
 * The handoff code is spent the moment it is redeemed, but the link that
 * carries it stays in whatever chat it was pasted into and gets clicked again
 * -- a second monitor set up later, a tab reopened, the same link tapped on the
 * iPad the next evening. Every one of those used to hit a dead end, and in the
 * first 18 hours 139 of them did, three quarters as repeat attempts from a
 * device that had already been here.
 *
 * So the redemption is remembered per device. Clicking the same link again
 * never reaches the server, which means the code can stay strictly single-use:
 * nothing here helps anyone who did not redeem it in this browser.
 *
 * localStorage, not sessionStorage: the point is to survive the tab closing.
 * The key deliberately does not share a prefix with `callout:credential` --
 * `forgetCredential()` matches on prefix, and clearing one must not silently
 * clear the other.
 */
const HANDOFF_KEY = "callout:handoff";

/** Matches the signed session's own life; a stale one is refused with 4001. */
const HANDOFF_TTL_MS = 8 * 60 * 60 * 1000;

interface RememberedHandoff {
  code: string;
  session: string;
  user: AppUser;
  instanceId: string;
  savedAt: number;
}

/**
 * Pass a code to ask "did this browser already redeem *that* link"; pass
 * nothing to ask "is this browser still holding a handed-off session at all",
 * which is what a plain reload needs -- the address bar was scrubbed, so the
 * code is no longer there to ask about.
 */
function recallHandoff(code?: string): RememberedHandoff | null {
  try {
    const raw = window.localStorage.getItem(HANDOFF_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw) as RememberedHandoff;
    if (typeof cached?.session !== "string" || typeof cached.savedAt !== "number") return null;
    if (cached.savedAt + HANDOFF_TTL_MS < Date.now()) return null;
    if (code && cached.code !== code.toUpperCase()) return null;
    return cached;
  } catch {
    return null;
  }
}

function rememberHandoff(value: RememberedHandoff): void {
  try {
    window.localStorage.setItem(HANDOFF_KEY, JSON.stringify(value));
  } catch {
    // A private window refusing storage means the old dead end, not a failure.
  }
}

function forgetHandoff(): void {
  try {
    window.localStorage.removeItem(HANDOFF_KEY);
  } catch {
    // Nothing to do.
  }
}

/**
 * Called when the server rejects it, so the next load does not reuse a dud.
 *
 * Clears every cached credential rather than one: a tab only ever talks to one
 * application, and taking a client id here would mean threading it through the
 * socket code for no gain.
 */
export function forgetCredential(): void {
  try {
    for (const key of Object.keys(window.sessionStorage)) {
      if (key.startsWith(CREDENTIAL_KEY)) window.sessionStorage.removeItem(key);
    }
  } catch {
    // Nothing to do.
  }
  // A handed-off session is a clone of the one just rejected, so it is dead
  // too. Keeping it would hand the same dud back on the very next load.
  forgetHandoff();
}

async function bootstrapDiscord(config: ServerConfig): Promise<AppSession> {
  if (!config.clientId) {
    throw new Error("DISCORD_CLIENT_ID is not configured on the server");
  }

  const sdk = new DiscordSDK(config.clientId);
  sdkRef = sdk;
  await sdk.ready();

  // Match the language the person already reads Discord in. Not fatal if the
  // client refuses: the browser's own language is a decent second guess.
  const discordLocale = await sdk.commands
    .userSettingsGetLocale()
    .then((result) => result.locale)
    .catch(() => null);
  initLocale(discordLocale);

  // Reuse this tab's credential when it still has one; see readCredential.
  let result: SessionResponse;
  const cached = readCredential(config.clientId);

  if (cached) {
    result = {
      session: cached.session,
      trace: cached.trace ?? undefined,
      user: cached.user,
      access_token: cached.accessToken ?? undefined,
    };
  } else {
    const { code } = await sdk.commands.authorize({
      client_id: config.clientId,
      response_type: "code",
      state: "",
      prompt: "none",
      scope: ["identify"],
    });

    result = await postJson<SessionResponse>("/api/token", { code });

    writeCredential(config.clientId, {
      session: result.session,
      trace: result.trace ?? null,
      user: result.user,
      accessToken: result.access_token ?? null,
      // Well inside the server's twelve hours, so a reused one is never stale.
      expiresAt: Date.now() + 8 * 60 * 60 * 1000,
    });
  }

  // Completes the handshake so Discord considers the Activity authenticated.
  // Cheap and local -- it is the token *exchange* that Discord rate limits.
  if (result.access_token) {
    await sdk.commands.authenticate({ access_token: result.access_token }).catch(() => {
      // A refused token means the cached one has gone stale; drop it so the
      // next load does a fresh exchange rather than looping on a dud.
      forgetCredential();
    });
  }

  const pip = await enableInteractivePip(sdk);

  return {
    session: result.session,
    user: result.user,
    /**
     * The voice channel, not the activity instance. Discord hands out a fresh
     * instance id when someone pops the activity out into its own window, which
     * would drop them into an empty room; the channel id survives that, and a
     * relaunch later in the same call rejoins the same room.
     */
    instanceId: sdk.channelId ?? sdk.instanceId,
    inDiscord: true,
    feedbackEnabled: config.feedback === true,
    publicUrl: config.publicUrl ?? "",
    pip,
    trace: result.trace ?? null,
  };
}

/**
 * Browser fallback used while developing. The room key comes from `?room=`, so
 * two tabs with the same code can be used to check the syncing.
 */
interface WebSessionResponse extends SessionResponse {
  instanceId: string;
  roomUrl: string;
}

/**
 * Coming back from Discord's sign-in page.
 *
 * The redirect lands on our own origin -- the address the application is
 * registered with -- so the code arrives as a query on an ordinary page load.
 * It is spent immediately and scrubbed off the address bar, which is then
 * rewritten to the room: from here on the address bar is the invitation, and
 * a reload or a bookmark comes straight back to the same map.
 */
async function bootstrapWebSignIn(config: ServerConfig, code: string, state: string) {
  const result = await postJson<WebSessionResponse>("/api/web-session", { code, state });
  window.history.replaceState(null, "", `/?room=${encodeURIComponent(result.instanceId)}`);
  return {
    session: result.session,
    user: result.user,
    instanceId: result.instanceId,
    inDiscord: false,
    feedbackEnabled: config.feedback === true,
    publicUrl: config.publicUrl ?? "",
    roomUrl: result.roomUrl,
    trace: result.trace ?? null,
  } satisfies AppSession;
}

async function bootstrapBrowser(config: ServerConfig): Promise<AppSession> {
  // Redeeming scrubbed `/j/<code>` off the address bar, so a reload of the
  // browser window arrives here with nothing to identify it. Without this, the
  // window someone set up on their second monitor turns into the landing page
  // the first time it is refreshed.
  const remembered = recallHandoff();
  if (remembered) {
    return {
      ...sessionFromHandoff(remembered, null),
      feedbackEnabled: config.feedback === true,
      publicUrl: config.publicUrl ?? "",
    };
  }

  const query = new URLSearchParams(window.location.search);
  const code = query.get("code");
  const state = query.get("state");
  if (code && state) return bootstrapWebSignIn(config, code, state);

  /*
   * Somebody followed a link to a room made in a browser. They cannot join
   * without a name the room can put on their pins, so they go and sign in;
   * the room they were heading for rides through in the `state`.
   */
  const wanted = query.get("room");
  if (!config.allowAnonymous && wanted?.startsWith("web-")) {
    window.location.replace(`/login?room=${encodeURIComponent(wanted)}`);
    // The redirect has already been asked for; nothing here will render.
    return new Promise<AppSession>(() => {});
  }

  if (!config.allowAnonymous) {
    // Not an error: this is someone who followed a shared link, or who came
    // looking. The App turns it into the landing page rather than a failure.
    throw new NotInDiscord(config.installUrl ?? "");
  }

  const params = query;
  const room = params.get("room") ?? "local-dev";

  const stored = window.localStorage.getItem("callout:name");
  const name = params.get("name") ?? stored ?? `Guest ${Math.floor(Math.random() * 900 + 100)}`;
  window.localStorage.setItem("callout:name", name);

  const result = await postJson<SessionResponse>("/api/dev-session", { name });
  return {
    session: result.session,
    user: result.user,
    instanceId: room,
    inDiscord: false,
    feedbackEnabled: config.feedback === true,
    publicUrl: config.publicUrl ?? "",
    trace: result.trace ?? null,
  };
}

interface HandoffResponse extends SessionResponse {
  instanceId: string;
}

/**
 * An invite link needs a name before it can be redeemed, so unlike the handoff
 * it cannot be resolved during boot. The App asks first, then calls
 * `joinViaInvite`.
 */
export function inviteCodeFromUrl(): string | null {
  return /^\/i\/([0-9A-Za-z]{4,32})\/?$/.exec(window.location.pathname)?.[1] ?? null;
}

export async function joinViaInvite(code: string, name: string): Promise<AppSession> {
  const config = await getJson<ServerConfig>("/api/config");
  const result = await postJson<HandoffResponse>("/api/invite/redeem", { code, name });
  window.localStorage.setItem("callout:name", name);
  window.history.replaceState(null, "", "/");
  return {
    session: result.session,
    user: result.user,
    instanceId: result.instanceId,
    inDiscord: false,
    feedbackEnabled: config.feedback === true,
    publicUrl: config.publicUrl ?? "",
    trace: result.trace ?? null,
  };
}

/**
 * Landing here means someone opened the "browser window" link from inside the
 * Activity. The code in the path is the only credential, and it is spent on
 * first use, so the address bar is scrubbed once it has been traded in.
 */
async function bootstrapHandoff(code: string): Promise<AppSession> {
  const remembered = recallHandoff(code);
  if (remembered) {
    // Already traded in from this browser. Going back to the server would only
    // burn a code that is gone and answer with an error the reader cannot act
    // on -- they are exactly where they asked to be.
    window.history.replaceState(null, "", "/");
    return sessionFromHandoff(remembered, null);
  }

  const result = await postJson<HandoffResponse>("/api/handoff/redeem", { code });
  rememberHandoff({
    code: code.toUpperCase(),
    session: result.session,
    user: result.user,
    instanceId: result.instanceId,
    savedAt: Date.now(),
  });
  window.history.replaceState(null, "", "/");
  return sessionFromHandoff(
    { code, session: result.session, user: result.user, instanceId: result.instanceId, savedAt: Date.now() },
    result.trace ?? null,
  );
}

function sessionFromHandoff(handoff: RememberedHandoff, trace: string | null): AppSession {
  return {
    session: handoff.session,
    user: handoff.user,
    instanceId: handoff.instanceId,
    inDiscord: false,
    feedbackEnabled: false,
    publicUrl: "",
    trace,
  };
}

export async function bootstrap(): Promise<AppSession> {
  // A sensible default before Discord gets a say, so nothing renders in the
  // wrong language for the split second before the handshake finishes.
  initLocale(null);

  const handoff = /^\/j\/([0-9A-Za-z]{4,32})\/?$/.exec(window.location.pathname)?.[1];
  if (handoff) {
    const config = await getJson<ServerConfig>("/api/config");
    const session = await bootstrapHandoff(handoff);
    return {
      ...session,
      feedbackEnabled: config.feedback === true,
      publicUrl: config.publicUrl ?? "",
    };
  }

  const config = await getJson<ServerConfig>("/api/config");
  return IN_DISCORD ? bootstrapDiscord(config) : bootstrapBrowser(config);
}
